import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { MoodboardService } from './moodboard.service';

export interface GenerationJob {
  entityVersionId: any;
  moodboardId: any;
  varCountBefore: number;
  entityName: string;
}

export interface GenFabEntry {
  imageId: number;
  entityVersionId: any;
  moodboardId: any;
  entityName: string;
  status: 'processing' | 'completed';
}

@Injectable({ providedIn: 'root' })
export class MoodboardGenerationService {
  private jobs = new Map<number, GenerationJob>();
  private completedJobs = new Map<number, GenerationJob>(); // retained for FAB display
  private timers = new Map<number, any>();

  // Signals — reactive in Angular templates automatically
  processingIds = signal<Set<number>>(new Set());
  completedIds = signal<Set<number>>(new Set());

  /** Emits imageId when background polling detects generation is complete */
  onComplete$ = new Subject<number>();
  /** Emits imageId when polling times out or an error forces failure */
  onFail$ = new Subject<number>();

  constructor(private moodboardService: MoodboardService) {}

  isProcessing(imageId: number): boolean {
    return this.processingIds().has(imageId);
  }

  isCompleted(imageId: number): boolean {
    return this.completedIds().has(imageId);
  }

  /** Returns true if any image belonging to this entity version is being generated.
   *  Reading processingIds() registers this as a signal dependency so templates re-evaluate reactively. */
  hasProcessingForVersion(entityVersionId: any): boolean {
    // Touch the signal so Angular's change detection tracks this call
    this.processingIds();
    for (const [, job] of this.jobs) {
      if (job.entityVersionId == entityVersionId) return true;
    }
    return false;
  }

  startPolling(imageId: number, entityVersionId: any, moodboardId: any, varCountBefore: number, entityName = ''): void {
    this.jobs.set(imageId, { entityVersionId, moodboardId, varCountBefore, entityName });
    this.processingIds.update(s => new Set([...s, imageId]));
    this.completedIds.update(s => { const n = new Set(s); n.delete(imageId); return n; });
    this._doPoll(imageId, 0);
  }

  markProcessing(imageId: number, entityVersionId: any, moodboardId: any, varCountBefore: number, entityName = ''): void {
    this.jobs.set(imageId, { entityVersionId, moodboardId, varCountBefore, entityName });
    this.processingIds.update(s => new Set([...s, imageId]));
    this.completedIds.update(s => { const n = new Set(s); n.delete(imageId); return n; });
  }

  markFailed(imageId: number): void {
    this.jobs.delete(imageId);
    this._clearTimer(imageId);
    this.processingIds.update(s => { const n = new Set(s); n.delete(imageId); return n; });
    this.onFail$.next(imageId);
  }

  clearCompleted(imageId: number): void {
    this.completedJobs.delete(imageId);
    this.completedIds.update(s => { const n = new Set(s); n.delete(imageId); return n; });
  }

  clearAll(imageId: number): void {
    this.jobs.delete(imageId);
    this.completedJobs.delete(imageId);
    this._clearTimer(imageId);
    this.processingIds.update(s => { const n = new Set(s); n.delete(imageId); return n; });
    this.completedIds.update(s => { const n = new Set(s); n.delete(imageId); return n; });
  }

  /** Marks an in-flight job as completed.
   *  Used when the caller polls job-status externally (instead of polling variations length). */
  markCompleted(imageId: number): void {
    this._markComplete(imageId);
  }

  /** Returns all active + completed generation entries for FAB display (reactive via signals) */
  getFabEntries(): GenFabEntry[] {
    this.processingIds(); // signal dependency
    this.completedIds();  // signal dependency
    const entries: GenFabEntry[] = [];
    for (const [imageId, job] of this.jobs) {
      entries.push({ imageId, ...job, status: 'processing' });
    }
    for (const [imageId, job] of this.completedJobs) {
      entries.push({ imageId, ...job, status: 'completed' });
    }
    return entries;
  }

  private _doPoll(imageId: number, attempt: number): void {
    if (attempt >= 120) {
      this.markFailed(imageId);
      return;
    }
    const timer = setTimeout(() => {
      const job = this.jobs.get(imageId);
      if (!job) return;
      const payload = { moodboard_id: job.moodboardId, version_id: job.entityVersionId };
      this.moodboardService.getEntityDetails(payload).subscribe((res: any) => {
        if (res?.statusCode === 200) {
          const freshImages = res.data?.entityVersion?.images || [];
          const freshItem = freshImages.find((img: any) => img.id === imageId);
          const currentCount = freshItem?.variations?.length || 0;
          if (currentCount > job.varCountBefore) {
            this._markComplete(imageId);
          } else {
            this._doPoll(imageId, attempt + 1);
          }
        } else {
          this._doPoll(imageId, attempt + 1);
        }
      });
    }, 3000);
    this.timers.set(imageId, timer);
  }

  private _markComplete(imageId: number): void {
    const job = this.jobs.get(imageId);
    if (job) this.completedJobs.set(imageId, job); // retain for FAB display
    this.jobs.delete(imageId);
    this._clearTimer(imageId);
    this.processingIds.update(s => { const n = new Set(s); n.delete(imageId); return n; });
    this.completedIds.update(s => new Set([...s, imageId]));
    this.onComplete$.next(imageId);
  }

  private _clearTimer(imageId: number): void {
    const t = this.timers.get(imageId);
    if (t) clearTimeout(t);
    this.timers.delete(imageId);
  }
}
